import { getApproximateLabel } from './taskLogic';
import { TASK_STATUSES, daysUntil, getTaskStatus, timeIntervalParts } from './taskStatus';
import { formatDate, formatDisplayDate } from './dateFormat';
import { expectedText, hasPlanned, plannedText } from './planning';

const dayCount = (n) => `${n} ${n === 1 ? 'day' : 'days'}`;

// The badge's words: the status with how many days, e.g. "Overdue · 12 days", "Due today",
// "Due soon · in 2 days", "This week · in 5 days", "Later · in 23 days"; in a planned week, month position or
// season "This week · until 15-11-2026" (its last day, from which overdue counts); timing in words as written
// (e.g. "BBCH 89"); "No date" when there is none (empty, "?" or "TBD")
function badgeText(status, days, timeInterval, dueEnd, today) {
  const daysToEnd = daysUntil(dueEnd, today) ?? days;
  switch (status) {
    case 'overdue': return `Overdue · ${dayCount(Math.abs(daysToEnd))}`;
    case 'soon': return days === 0 ? 'Due today' : `Due soon · in ${dayCount(days)}`;
    case 'week': return days > 0 ? `This week · in ${dayCount(days)}` : `This week · until ${formatDisplayDate(dueEnd)}`;
    case 'later': return `Later · in ${dayCount(days)}`;
    case 'text': return String(timeInterval).trim();
    default: return TASK_STATUSES[status].label;
  }
}

// The tooltip: the full due date (or the timing in words as written). With a Planned value: that value as
// written (and its date, for a month, week or season) and the customer's Time interval beside it, e.g.
// "Planned date medio May 2027 (15-05-2027) · Time interval: 15-04-2027".
function plannedDetail(task) {
  // The derived date of a month, week or season (words have none, a plain date is its own)
  const derived = task.planned_interval && task.planned_date && !/^\d{4}-\d{2}-\d{2}$/.test(task.planned_interval)
    ? formatDate(task.planned_date) : null;
  return `Planned date ${plannedText(task)}${derived ? ` (${derived})` : ''} · Time interval: ${expectedText(task) || '—'}`;
}

function badgeDetail(status, dueDate, timeInterval, words) {
  if (status === 'none') return 'No due date';
  if (status === 'text') return String(words);
  const approximate = getApproximateLabel(timeInterval);
  const date = formatDisplayDate(dueDate) ?? formatDate(timeInterval);
  return approximate ? `${approximate} (${date})` : `Due ${date}`;
}

/**
 * A task's status badge: the status (utils/taskStatus.js - the same rules and colours everywhere)
 * with the number of days (`text`), the full due date for its tooltip (`detail`), and the classes
 * for a badge, a left border and a background.
 * @param {{ dueDate?, timeInterval?, timeinterval?, donedate?, cancelled? }} task - an enriched task
 */
export function getTaskBadge(task = {}, today = new Date()) {
  const timeInterval = task.timeInterval ?? task.timeinterval;
  // Without any date, a Planned value in words (e.g. "after harvest") is what the badge says, else the Time
  // interval's text (it never gives a date)
  const words = !task.dueDate && task.planned_interval ? task.planned_interval : timeIntervalParts(task).text;
  const status = getTaskStatus({ ...task, timeinterval: words }, today);
  const style = TASK_STATUSES[status];
  return {
    status,
    text: badgeText(status, daysUntil(task.dueStart ?? task.dueDate, today), words, task.dueEnd, today),
    detail: status === 'done'
      ? `Done ${formatDisplayDate(task.donedate) ?? ''}`.trim()
      : hasPlanned(task) ? plannedDetail(task) : badgeDetail(status, task.dueDate, timeInterval, words),
    color: style.badge,
    borderColor: style.border,
    surfaceColor: style.surface,
  };
}
